import { ObjectTools } from '@sol/components/tools/object-tools';
import { SolTooltipEntry, SolTooltipPosition } from '@sol/components';
import { SolText } from '@sol/components/models/core/sol-text';

export interface SolTooltipOverlay {
  readonly id: string;
  readonly text: SolText;
  readonly left: number;
  readonly top: number;
  readonly transform: string;
  readonly position: SolTooltipPosition;
  readonly exiting: boolean;
}

export class SolTooltipsState {
  private constructor(
    public readonly tooltips: SolTooltipEntry[],
    public readonly overlays: SolTooltipOverlay[],
    public readonly visible: boolean,
  ) {}

  public static of(): SolTooltipsState {
    return new SolTooltipsState([], [], false);
  }

  public withTooltips(tooltips: SolTooltipEntry[]): SolTooltipsState {
    const activeIds = new Set(tooltips.map(t => t.id));
    // Carry forward any currently-visible overlay whose id is no longer in the
    // active list, marked as exiting so the singleton can run the fade-out
    // animation before actually removing it. If an exiting overlay's id shows
    // up again (rapid re-hover), the rebuild below resurrects it cleanly.
    const exitingCarryovers = this.overlays
      .filter(o => !activeIds.has(o.id))
      .map(o => (o.exiting ? o : { ...o, exiting: true }));
    const incoming = tooltips.map(entry => SolTooltipsState.toOverlay(entry));
    const overlays = [...incoming, ...exitingCarryovers];
    return this.copy({
      tooltips,
      overlays,
      visible: overlays.length > 0,
    });
  }

  public withOverlayRemoved(id: string): SolTooltipsState {
    const overlays = this.overlays.filter(o => o.id !== id);
    return this.copy({ overlays, visible: overlays.length > 0 });
  }

  private static toOverlay(entry: SolTooltipEntry): SolTooltipOverlay {
    const gap = 8;
    const bounds = entry.triggerBounds;
    const pos = entry.position;

    let left: number;
    let top: number;
    let transform: string;

    switch (pos) {
      case 'bottom':
        left = bounds.left + bounds.width / 2;
        top = bounds.bottom + gap;
        transform = 'translateX(-50%)';
        break;
      case 'top':
        left = bounds.left + bounds.width / 2;
        top = bounds.top - gap;
        transform = 'translate(-50%, -100%)';
        break;
      case 'left':
        left = bounds.left - gap;
        top = bounds.top + bounds.height / 2;
        transform = 'translate(-100%, -50%)';
        break;
      case 'right':
        left = bounds.right + gap;
        top = bounds.top + bounds.height / 2;
        transform = 'translateY(-50%)';
        break;
    }

    return { id: entry.id, text: entry.text, left, top, transform, position: pos, exiting: false };
  }

  public withViewportClamp(id: string, rect: DOMRect): SolTooltipsState {
    const margin = 8;
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;
    const entry = this.tooltips.find(t => t.id === id);
    if (!entry) {
      return this;
    }

    const overlay = this.overlays.find(o => o.id === id);
    if (!overlay) {
      return this;
    }

    // Check if tooltip fits; if not try opposite position
    const fits = SolTooltipsState.fitsInViewport(rect, viewportWidth, viewportHeight, margin);
    if (fits) {
      // Clamp within viewport
      const clamped = SolTooltipsState.clamp(overlay, rect, viewportWidth, viewportHeight, margin, entry.position);
      if (clamped.left === overlay.left && clamped.top === overlay.top) {
        return this;
      }
      const overlays = this.overlays.map(o => (o.id === id ? clamped : o));
      return this.copy({ overlays });
    }

    // Flip to opposite position
    const flipped = SolTooltipsState.flipPosition(entry.position);
    const flippedOverlay = SolTooltipsState.toOverlay({ ...entry, position: flipped });
    const overlays = this.overlays.map(o => (o.id === id ? flippedOverlay : o));
    return this.copy({ overlays });
  }

  private static flipPosition(pos: SolTooltipPosition): SolTooltipPosition {
    switch (pos) {
      case 'top':
        return 'bottom';
      case 'bottom':
        return 'top';
      case 'left':
        return 'right';
      case 'right':
        return 'left';
    }
  }

  private static fitsInViewport(rect: DOMRect, viewportWidth: number, viewportHeight: number, margin: number): boolean {
    return (
      rect.left >= margin &&
      rect.right <= viewportWidth - margin &&
      rect.top >= margin &&
      rect.bottom <= viewportHeight - margin
    );
  }

  private static clamp(
    overlay: SolTooltipOverlay,
    rect: DOMRect,
    viewportWidth: number,
    viewportHeight: number,
    margin: number,
    position: SolTooltipPosition,
  ): SolTooltipOverlay {
    let left = overlay.left;
    let top = overlay.top;

    // Horizontal clamping for top/bottom positioned tooltips
    if (position === 'top' || position === 'bottom') {
      const overflowRight = rect.right - (viewportWidth - margin);
      const overflowLeft = margin - rect.left;
      if (overflowRight > 0) {
        left -= overflowRight;
      } else if (overflowLeft > 0) {
        left += overflowLeft;
      }
    }

    // Vertical clamping for left/right positioned tooltips
    if (position === 'left' || position === 'right') {
      const overflowBottom = rect.bottom - (viewportHeight - margin);
      const overflowTop = margin - rect.top;
      if (overflowBottom > 0) {
        top -= overflowBottom;
      } else if (overflowTop > 0) {
        top += overflowTop;
      }
    }

    return { ...overlay, left, top };
  }

  private copy(props: Partial<SolTooltipsState>): SolTooltipsState {
    return ObjectTools.copy<SolTooltipsState>(this, props);
  }
}
